/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Teasers {...cmsData} />)
 * and then make these props fit the final module.
 */
import React from "react"
import { Section, CmsImage } from "@bw/bits"
import { Teasers as TeasersModule } from "@bw/modules"
import { Teaser } from "@bw/partials"
import { useStaticQuery, graphql } from "gatsby"
import { useLocalization } from "gatsby-theme-i18n"
import dateFormat from "../../../utils/dateFormat"
import shuffle from "../../../utils/shuffle"

const TeasersLatest = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  backgroundVariant,
  postType,
  maxItems,
  numberOfColumns,
  composition,
}) => {
  const { locale } = useLocalization()
  const data = useStaticQuery(graphql`
    query LatestItemsInCollection {
      pages: allCollectionPages(limit: 50) {
        nodes {
          locale
          title
          url
          teaserImage {
            alt
            localImage {
              childImageSharp {
                gatsbyImageData(width: 800, height: 450)
              }
            }
          }
        }
      }
      articles: allCollectionArticles(limit: 50) {
        nodes {
          locale
          title
          suptitle: date
          url
          authors {
            name
            avatar {
              url
            }
          }
          teaserImage {
            alt
            localImage {
              childImageSharp {
                gatsbyImageData(width: 800, height: 450)
              }
            }
          }
        }
      }
    }
  `)

  let items = postType
    .reduce((acc, type) => {
      acc = [...acc, ...data[type.value].nodes]
      return acc
    }, [])
    .filter(item => item.locale === locale)

  if (composition.value === "random") {
    items = shuffle(items)
  }

  if (maxItems) {
    items = items.slice(0, maxItems)
  }

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        backgroundVariant,
      }}
    >
      <TeasersModule numberOfColumns={parseInt(numberOfColumns.value)}>
        {items.map((item, i) => {
          let author = undefined
          if (item.authors) {
            author = {
              name: item.authors[0]?.name,
              image: (
                <CmsImage
                  src={item.authors[0]?.avatar?.url}
                  alt={item.authors[0]?.name || ""}
                />
              ),
            }
          }

          return (
            <Teaser
              key={i}
              suptitle={dateFormat(item.suptitle, locale)}
              title={item.title}
              image={
                item.teaserImage ? (
                  <CmsImage
                    src={item.teaserImage}
                    alt={item.teaserImage?.alt || item.title}
                  />
                ) : null
              }
              to={item.url}
              author={author}
            />
          )
        })}
      </TeasersModule>
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on TeasersLatestModule {
 *   ...TeasersLatestModuleFragment
 * }
 *
 * This is also what you will get in the props of your {{pascalCase name}}
 * resolver component.
 */
export const query = graphql`
  fragment TeasersLatestModuleFragment on TeasersLatestModule {
    composition {
      value
    }
    maxItems
    postType {
      value
    }
    numberOfColumns {
      value
    }
  }
`

export { TeasersLatest }
